<template>
  <div>
    <ErrorAlert
      :show="alert.error"
      :message="alert.message"
      @hide-alert="hideError"
    />
    <SuccessAlert
      :show="alert.success"
      :message="alert.message"
      @hide-alert="hideSuccess"
    />
    <b-overlay :show="processing" rounded="sm" class="overlay-display">
      <template #overlay>
        <div class="text-center">
          <b-spinner type="grow" variant="info" label="Loading..."></b-spinner>
          <p id="cancel-label">Please wait...</p>
        </div>
      </template>
      <h1>Create New Product</h1>
      <p>* = required fields</p>
      <div>
        <label class="mr-1">*Template Product:</label
        ><b-icon id="template-prod-info" icon="info-circle"></b-icon>
        <b-tooltip target="template-prod-info" class="font-lg"
          >The id of the product used as a template. Found in the product url.
          Example: Product id 6932111523905 is found from
          https://admin.shopify.com/store/callia-flowers/products/6932111523905
        </b-tooltip>
        <b-form-input v-model="product.prodTemplateId" type="text">{{
          product.prodTemplateId
        }}</b-form-input>
        <br />
      </div>
      <div>
        <label>*Regions:</label>
        <b-form-tags
          size="lg"
          tag-variant="primary"
          input-id="tags-basic"
          v-model="product.regions"
        ></b-form-tags>
        <br />
      </div>
      <div>
        <label class="mr-1">*Weeks:</label
        ><b-icon id="weeks-info" icon="info-circle"></b-icon>
        <b-tooltip target="weeks-info" class="font-lg"
          >Needs to be in format WWYY where WW is the week number and YY is the
          last two digits of the year. Example: 3225 for 32nd week of
          2025.</b-tooltip
        >
        <b-form-tags
          size="lg"
          tag-variant="primary"
          input-id="tags-basic"
          v-model="product.weekCodes"
        ></b-form-tags>
        <br />
      </div>
      <div>
        <label>Extra Tags:</label>
        <b-form-tags
          size="lg"
          tag-variant="primary"
          input-id="tags-basic"
          v-model="product.extraTags"
        ></b-form-tags>
        <br />
      </div>
      <div>
        <label class="mr-2">Product Type:</label><br />
        <b-dropdown
          dropdown
          id="product-type"
          variant="primary"
          :text="productTypeLabel"
        >
          <b-dropdown-item
            @click="selectProductType(item)"
            v-for="(item, index) in Object.keys(suggestedTagsByProdType)"
            :key="index"
            >{{ item }}</b-dropdown-item
          >
        </b-dropdown>
      </div>
      <div>
        <br />
        <label class="mr-2">Quantity Per Variant:</label>
        <b-icon id="quantity-info" icon="info-circle"></b-icon>
        <b-tooltip target="quantity-info" class="font-lg"
          >Each variant will have this quantity. If you want to use the
          quantities from the template product, leave this blank. If you want
          different quantities for each variant, set them up in the template
          product in Shopify and leave this blank.
        </b-tooltip>
        <b-form-input
          v-model="product.quantityPerVariant"
          id="quantity-per-variant"
          type="number"
        ></b-form-input>
        <br />
      </div>
      <div>
        <label class="mr-1">*Product SKU:</label
        ><b-icon id="product-sku-info" icon="info-circle"></b-icon>
        <b-tooltip target="product-sku-info" class="font-lg"
          >Needs to be lowercase with no spaces and no underscores. Must be less
          than 32 characters. Examples: surprise, dainty-giftset, lovely. Make
          sure the URL handle in the Search engine listing of the template
          product is the same as this handle. It can be found at the very bottom
          of the template product in shopify.
        </b-tooltip>
        <b-form-input v-model="product.handle" type="text">{{
          product.handle
        }}</b-form-input>
        <br />
      </div>
      <div>
        <label class="mr-2">*Add Week To SKU:</label
        ><b-icon id="add-week-to-sku-info" icon="info-circle"></b-icon>
        <b-tooltip target="add-week-to-sku-info" class="font-lg"
          >Make sure to set this to true if the product is single week. Ie if
          the product needs a single product per week, set this to be true. If
          the product is multi week, set to be false.
        </b-tooltip>
        <br />
        <b-dropdown
          dropdown
          id="add-week-to-sku"
          variant="primary"
          :text="addWeekToSKULabel"
        >
          <b-dropdown-item @click="selectAddWeekToSKU(true)">{{
            true
          }}</b-dropdown-item>
          <b-dropdown-item @click="selectAddWeekToSKU(false)">{{
            false
          }}</b-dropdown-item>
        </b-dropdown>
        <br />
      </div>
      <div>
        <br />
        <label class="mr-2">*Add Variant Pictures:</label
        ><b-icon id="add-variant-to-pics-info" icon="info-circle"></b-icon>
        <b-tooltip target="add-variant-to-pics-info" class="font-lg"
          >Adds variant images to product variants. The images come from the
          images in the template product. IMPORTANT: Make sure the photos in the
          template product are in the correct order. Ie first photo is the image
          for first variant, second photo is image for second variant, and so
          on.</b-tooltip
        >
        <br />

        <b-dropdown
          dropdown
          id="add-week-to-sku"
          variant="primary"
          :text="addVariantPicturesLabel"
        >
          <b-dropdown-item @click="selectAddVariantPictures(true)">{{
            true
          }}</b-dropdown-item>
          <b-dropdown-item @click="selectAddVariantPictures(false)">{{
            false
          }}</b-dropdown-item>
        </b-dropdown>
        <br />
      </div>
      <div>
        <br />
        <br />

        <b-button class="mr-3 px-2" variant="danger" @click="resetData"
          >Reset</b-button
        >
        <b-button
          class="ml-3 px-2"
          variant="success"
          :disabled="!isValidProduct"
          @click="createProduct"
          >Submit</b-button
        >
      </div>
      <br />
      <br />

      <h2>FAQs</h2>
      <b-card v-for="(faq, index) in faqs" :key="index" class="mb-2" no-body>
        <b-card-header class="p-0 bg-transparent">
          <b-button
            v-b-toggle="'faq-' + index"
            variant="link"
            class="w-100 text-left"
          >
            {{ faq.question }}
          </b-button>
        </b-card-header>

        <b-collapse
          :id="'faq-' + index"
          accordion="faq-accordion"
          role="tabpanel"
        >
          <b-card-body class="">
            {{ faq.answer }}
          </b-card-body>
        </b-collapse>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import ErrorAlert from "@/components/alerts/error.vue";
import SuccessAlert from "@/components/alerts/success.vue";

export default {
  name: "ProductCreation",
  computed: {
    productTypeLabel() {
      return this.product.productType
        ? this.product.productType
        : "Product Type";
    },
    addWeekToSKULabel() {
      return this.product.addWeekToSKU.toString() &&
        this.product.addWeekToSKU.toString().length > 0
        ? this.product.addWeekToSKU.toString()
        : "";
    },
    addVariantPicturesLabel() {
      return this.product.addVariantPictures.toString() &&
        this.product.addVariantPictures.toString().length > 0
        ? this.product.addVariantPictures.toString()
        : "";
    },
    isValidProduct() {
      return (
        this.product.prodTemplateId.length > 0 &&
        this.product.regions.length > 0 &&
        this.product.weekCodes.length > 0 &&
        this.product.handle.length > 0 &&
        this.product.extraTags.length >= 0
      );
    }
  },
  components: {
    SuccessAlert,
    ErrorAlert
  },
  data() {
    return {
      alert: {
        message: "",
        error: false,
        success: false
      },
      processing: false,
      product: {
        productType: "",
        prodTemplateId: "",
        regions: ["BC", "AB", "CG", "SK", "MB", "TR", "MT", "HX"],
        weekCodes: [],
        handle: "",
        quantityPerVariant: "",
        extraTags: [],
        addWeekToSKU: true,
        addVariantPictures: true,
        requireShipping: false
      },
      suggestedTagsByProdType: {
        "Flowers (Pastel)": [
          "bouquet",
          "Signature",
          "Addons",
          "general",
          "current",
          "Style:Pastel"
        ],
        "Flowers (Bright)": [
          "bouquet",
          "Signature",
          "Addons",
          "general",
          "current",
          "Style:Bright"
        ],
        "Flowers (Neutral)": [
          "bouquet",
          "Signature",
          "Addons",
          "general",
          "current",
          "Style:Neutral"
        ],
        "Flowers (Luxe)": [
          "bouquet",
          "Signature",
          "Addons",
          "general",
          "current",
          "Luxe"
        ],
        "Gift-set (Single Week)": [
          "Bundle",
          "Signature",
          "occasion:all",
          "NoVase",
          "Addons",
          "general"
        ],
        "Gift-set (Multi Week)": [
          "Bundle",
          "multiweek",
          "Signature",
          "occasion:all",
          "NoVase",
          "Addons",
          "general"
        ],
        Subscription: [
          "Subscription",
          "BC",
          "AB",
          "CG",
          "SK",
          "MB",
          "TR",
          "MT",
          "HX"
        ],
        Vase: ["vase", "active", "occasion:all"],
        "Vase (Ongoing)": ["vase", "active", "occasion:all", "vase-ongoing"],
        "Gift-adds": ["vase", "active", "Addons", "gift-adds", "occasion:all"]
      },
      faqs: [
        {
          question: "Product landing page is showing product not found",
          answer:
            "Check that the template product has the right tags, make sure butter is set up properly and is published, and finally ensure that the URL handle in the Search engine listing of the template product matches the product handle you entered."
        },
        {
          question:
            "Why does the URL handle in the Search engine listing of the template product keep adding a number? ",
          answer:
            "If you enter 'surprise' and it shows as 'surprise-1' or something like that, you need to find the original product that has the correct URL handle and either delete it or update it to something different."
        },
        {
          question: "Not all my products were uploaded",
          answer:
            "There could be a couple issues. The most common issue is that the function in AWS that creates the products timed out. This can occur if you are uploading products for a lot of weeks/regions. If this happens, send the same request to create the products again and it will continue from where it left off. Don't worry about duplicates, the function will check for existing products and skip them. If you are still having issues, please contact dev."
        },
        {
          question: "How do I upload a multi-week product?",
          answer:
            "A multi-week product is a product that has multiple week tags. It won't have the week code in the SKU and it will have the 'multiweek' tag. To upload a multi-week product, add the first week code in the 'Weeks' field, then add the rest of the week codes in the 'Extra Tags' field. For example, if you want to upload a product for weeks 3225 and 3325, you would enter '3225' in the 'Weeks' field and '3325' in the 'Extra Tags' field."
        },
        {
          question: "The pictures for the variants are misaligned.",
          answer:
            "For example, the 'Standard' variant has the 'Deluxe' picture. This can happen if the images in 'Media' section of the template product are not in the correct order. Make sure that the first image in the template product is for the first variant, the second image is for the second variant, and so on. Note that the order of images in the 'Media' section is the order in which they will be used for the variants. Please contact dev and they will be able to fix the photos for each product."
        }
      ]
    };
  },
  methods: {
    async showAlert(errorObj) {
      console.log(errorObj);
      this.alert.message = errorObj.message;
      if (errorObj.isError) {
        this.alert.error = true;
        this.alert.success = false;
      } else {
        this.alert.error = false;
        this.alert.success = true;
      }
    },
    hideError() {
      this.alert.error = false;
    },
    hideSuccess() {
      this.alert.success = false;
    },
    async createProduct() {
      this.processing = true;
      event.preventDefault();
      await this.axios
        .post(
          process.env.VUE_APP_API_ADMIN_API_ENDPOINT +
            "/inventory/product/create",
          JSON.stringify(this.product),
          {
            headers: {
              Authorization: "Bearer " + this.$store.getters.idToken
            }
          }
        )
        .then(response => {
          if (response.status === 200) {
            this.showAlert({
              message:
                "Success: Request was successful and products are being created. Please check the products in Shopify.",
              isError: false
            });
          }
        })
        .catch(error => {
          this.showAlert({
            message: "Error: Something went wrong with creating the products.",
            isError: true
          });
          this.processing = false;
        });
      this.processing = false;
    },
    resetData() {
      this.product = {
        productType: "",
        prodTemplateId: "",
        regions: ["BC", "AB", "CG", "SK", "MB", "TR", "MT", "HX"],
        weekCodes: [],
        handle: "",
        quantityPerVariant: "",
        extraTags: [],
        addWeekToSKU: true,
        addVariantPictures: true,
        requireShipping: false
      };
    },
    selectProductType(type) {
      this.product.productType = type;
      this.product.extraTags = this.suggestedTagsByProdType[type] || [];
    },
    selectAddWeekToSKU(addWeek) {
      this.product.addWeekToSKU = addWeek;
    },
    selectAddVariantPictures(addPhoto) {
      this.product.addVariantPictures = addPhoto;
    }
  }
};
</script>

<style></style>
